import { createAction, createSelector, createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import _ from 'lodash';
import api from 'lib/api';
import utils from 'lib/utils';
import storage from 'lib/storage';
import notifications from 'lib/notifications';
import { discoveryBuyersSellersService } from 'services';

// Rows are identified by a composite natural key (see services/discoveryBuyersSellers
// transforms). `sort_by` is only the ranking value, so it drives ordering here instead
// of identity - keying on it used to collapse every row that shared a rank.
const adapter = createEntityAdapter({
  sortComparer: (a, b) => {
    const rankA = Number(_.get(a, 'sort_by')) || 0;
    const rankB = Number(_.get(b, 'sort_by')) || 0;
    if (rankA !== rankB) return rankA - rankB;
    return (Number(_.get(a, 'sort_index')) || 0) - (Number(_.get(b, 'sort_index')) || 0);
  },
});

// Monotonic id for every discovery request. Only the response belonging to the
// most recent request is allowed to write to the store, so a slow response for
// an older filter combination can never overwrite a newer one.
let requestSeq = 0;

const slice = createSlice({
  name: 'discoveryBuyersSellers',
  initialState: adapter.getInitialState({
    explorelist: [],
    busy: false,
    hasMore: true,
    commodity_count: [],
    total_count: 0,
  }),
  reducers: {
    setAll: adapter.setAll,
    addOne: adapter.addOne,
    addMany: adapter.addMany,
    updateOne: adapter.updateOne,
    updateMany: adapter.updateMany,
    upsertOne: adapter.upsertOne,
    upsertMany: adapter.upsertMany,
    removeOne: adapter.removeOne,
    removeMany: adapter.removeMany,
    removeAll: adapter.removeAll,
    setExplorelist: (state, { payload }) => ({ ...state, explorelist: payload }),
    setBusy(state, action) {
      let busy = action.payload;
      if (busy === false) {
        state.busy = false;
      } else {
        state.busy = true;
      }
    },
    setHasMore(state, action) {
      state.hasMore = action.payload;
    },
    setCommodityCount(state, action) {
      state.commodity_count = action.payload;
    },
    setTotalCount(state, action) {
      state.total_count = action.payload;
    },
  },
});

const fetchDiscoveryBuyersSellers = (params) => async (dispatch) => {
  const requestId = ++requestSeq;
  const offset = _.get(params, 'offset') || 0;
  const pageSize = _.get(params, 'page_Size') || 30;

  dispatch(slice.actions.setBusy(true));
  try {
    const resData = await discoveryBuyersSellersService.fetchDiscoveryBuyersSellers(params);

    // A newer request was fired while this one was in flight - drop this response.
    if (requestId !== requestSeq) {
      return;
    }

    const rows = _.get(resData, 'rows') || {};
    const rowCount = _.get(resData, 'rowCount') || 0;

    if (offset === 0) {
      // First page of a filter combination replaces the list instead of merging
      // into it, otherwise rows matching the previous filters stay on screen and
      // the rendered count drifts away from the count reported by the API.
      dispatch(slice.actions.setAll(rows));
    } else {
      dispatch(slice.actions.upsertMany(rows));
    }

    dispatch(slice.actions.setCommodityCount(_.get(resData, 'commodityCount') || []));
    dispatch(slice.actions.setTotalCount(_.get(resData, 'totalCount') || 0));
    dispatch(slice.actions.setHasMore(rowCount >= pageSize));
  } catch (err) {
    if (requestId !== requestSeq) {
      return;
    }
    dispatch(slice.actions.setAll({}));
    dispatch(slice.actions.setCommodityCount([]));
    dispatch(slice.actions.setTotalCount(0));
    dispatch(slice.actions.setHasMore(false));
    utils.displayErrors(err);
  } finally {
    if (requestId === requestSeq) {
      dispatch(slice.actions.setBusy(false));
    }
  }
};

const updateCommodityCount = (commodity_count) => async (dispatch) => {
  try {
    dispatch(slice.actions.setCommodityCount(commodity_count));
  } catch (err) {
    dispatch(slice.actions.setAll({}));
    utils.displayErrors(err);
  }
};
const actions = {
  ...slice.actions,
  fetchDiscoveryBuyersSellers,
  updateCommodityCount,
};

const selectors = {
  ...adapter.getSelectors((state) => state.discoveryBuyersSellers),
};

export { actions, selectors };

export default slice.reducer;
